import { Head, Link } from "@inertiajs/react"
import {
  GraduationCap,
  ShieldCheck,
  Briefcase,
  BookOpen,
  CircleCheck,
  Globe,
  ArrowLeft,
  Clock,
} from "lucide-react"

import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import MarketingLayout from "@/layouts/marketing-layout"
import { useFlashcardProps } from "@/hooks/use-flashcard-props"
import type { TeacherShowProps } from "@/types/teacher"

export default function TeacherShow(props: Record<string, unknown>) {
  const { teacher, pendingApproval } =
    useFlashcardProps<TeacherShowProps>(props)
  return (
    <MarketingLayout>
      <Head title={`${teacher.name} — Thai Teacher | ThaiCopilot`}>
        <meta
          name="description"
          content={`${teacher.name} is a verified Thai teacher on ThaiCopilot. ${teacher.bio?.slice(0, 120) || "Learn Thai with a qualified, verified teacher."}`}
        />
      </Head>

      <div className="min-h-screen bg-white dark:bg-slate-950">
        {/* Pending Approval Banner */}
        {pendingApproval && (
          <div className="border-b border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30">
            <div className="container mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8">
              <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-200">
                <Clock className="size-4 shrink-0" />
                <p>
                  <span className="font-medium">Profile pending approval.</span>{" "}
                  This is a preview of how your profile will look. It will be
                  visible to students once approved (48-72 hours).
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Back nav */}
        <div className="border-b border-slate-100 bg-slate-50/50 dark:border-slate-800/50 dark:bg-slate-900/50">
          <div className="container mx-auto max-w-4xl px-4 py-3 sm:px-6 lg:px-8">
            <Link
              href="/teachers"
              className="inline-flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
            >
              <ArrowLeft className="size-3.5" />
              All Teachers
            </Link>
          </div>
        </div>

        {/* Profile Header */}
        <div className="border-b border-slate-100 dark:border-slate-800">
          <div className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
            <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
              {/* Avatar */}
              <div className="rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 p-[3px] shadow-lg shadow-indigo-500/20">
                <Avatar className="size-20 border-[3px] border-white sm:size-24 dark:border-slate-950">
                  <AvatarFallback className="bg-indigo-100 text-2xl font-bold text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                    {teacher.avatarInitials}
                  </AvatarFallback>
                </Avatar>
              </div>

              {/* Name + Verification */}
              <div className="text-center sm:text-left">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl dark:text-white">
                  {teacher.name}
                </h1>
                <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {pendingApproval ? (
                    <Badge className="gap-1.5 bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400">
                      <Clock className="size-3.5" />
                      Pending Approval
                    </Badge>
                  ) : (
                    <Badge className="gap-1.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400">
                      <ShieldCheck className="size-3.5" />
                      Verified
                    </Badge>
                  )}
                  <Badge
                    variant="secondary"
                    className="gap-1.5 text-slate-600 dark:text-slate-400"
                  >
                    <GraduationCap className="size-3.5" />
                    Thai Teacher
                  </Badge>
                </div>

                {/* Credential Highlights */}
                {teacher.credentials && teacher.credentials.length > 0 && (
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5 sm:justify-start">
                    {teacher.credentials.map((credential) => (
                      <Badge
                        key={credential.id}
                        className="gap-1 border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-400"
                      >
                        <CircleCheck className="size-3" />
                        {credential.name}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Profile Body */}
        <div className="container mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="space-y-8 lg:col-span-2">
              {/* Bio */}
              {teacher.bio && (
                <section>
                  <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                    <BookOpen className="size-4" />
                    About
                  </h2>
                  <p className="whitespace-pre-wrap text-base leading-relaxed text-slate-700 dark:text-slate-300">
                    {teacher.bio}
                  </p>
                </section>
              )}

              {/* Teaching Experience */}
              {teacher.teachingExperience && (
                <section>
                  <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                    <GraduationCap className="size-4" />
                    Teaching Experience
                  </h2>
                  <p className="whitespace-pre-wrap text-base leading-relaxed text-slate-700 dark:text-slate-300">
                    {teacher.teachingExperience}
                  </p>
                </section>
              )}

              {/* Professional Background */}
              {teacher.professionalBackground && (
                <section>
                  <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
                    <Briefcase className="size-4" />
                    Professional Background
                  </h2>
                  <p className="whitespace-pre-wrap text-base leading-relaxed text-slate-700 dark:text-slate-300">
                    {teacher.professionalBackground}
                  </p>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Info Card */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 text-sm font-semibold text-slate-900 dark:text-white">
                  Quick Info
                </h3>
                <dl className="space-y-3 text-sm">
                  {teacher.englishProficiency && (
                    <div className="flex items-start gap-3">
                      <dt className="flex shrink-0 items-center gap-1.5 text-slate-400 dark:text-slate-500">
                        <Globe className="size-4" />
                      </dt>
                      <dd className="text-slate-700 dark:text-slate-300">
                        <span className="block text-xs text-slate-400 dark:text-slate-500">
                          English Proficiency
                        </span>
                        {teacher.englishProficiency}
                      </dd>
                    </div>
                  )}
                  <div className="flex items-start gap-3">
                    <dt className="flex shrink-0 items-center gap-1.5 text-slate-400 dark:text-slate-500">
                      {pendingApproval ? (
                        <Clock className="size-4" />
                      ) : (
                        <ShieldCheck className="size-4 text-emerald-500" />
                   )}
                    </dt>
                    <dd className="text-slate-700 dark:text-slate-300">
                      <span className="block text-xs text-slate-400 dark:text-slate-500">
                        Status
                      </span>
                      {pendingApproval
                        ? "Pending review"
                        : "Manually verified by ThaiCopilot"}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* CTA */}
              <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-violet-50 p-5 dark:border-indigo-900/50 dark:from-indigo-950/30 dark:to-violet-950/30">
                <p className="mb-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                  Interested in learning with {teacher.name.split(" ")[0]}?
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Booking and messaging coming soon. Sign up to be notified when
                  this teacher is available for classes.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MarketingLayout>
  )
}
